import { useForm } from '@nerdgeschoss/react-use-form-library';
import classnames from 'classnames';
import { compact } from 'lodash';
import React, { useEffect, useState } from 'react';
import './print_history.scss';

import { Card } from '../../../../../../components/Card/Card';
import { FormTagsInput } from '../../../../../../components/Form/TagsInput/FormTagsInput';
import { Icon } from '../../../../../../components/Icon/Icon';
import { Modal } from '../../../../../../components/Modal/Modal';
import { SortableList } from '../../../../../../components/SortableList/SortableList';
import { Stack } from '../../../../../../components/Stack/Stack';
import { Tag } from '../../../../../../components/Tag/Tag';
import { graphql } from '../../../../../../generated';
import { PrintingOrder as GeneratedPrintingOrder } from '../../../../../../generated/graphql';
import { t } from '../../../../../../locales/translate';
import { apollo, useQueryHandler } from '../../../../../../model/graphql';
import { useQueryAsState } from '../../../../../../model/history';
import { VenuePlan } from '../../../../../../model/venue';
import { formatDateTime, generateMailToLink, useSelection } from '../../../../../../util/util';
import { ResendEmailTickets } from './resend_email_tickets';
import { TicketReturn } from './TicketReturn';
import { OrderTickets, PrintedTicket } from './order_tickets';
import { PRINT_ORDER } from './order_tickets';
import { notNil } from '../../../../../../util/values';
import { TruncatedText } from '../../../../../../components/truncated_text/truncated_text';
import { Button } from '../../../../../../components/Button/Button';
import { useMap } from '../../../../../../util/react-util';
import { handleError } from '../../../../../../model/notification';
import { Spinner } from '../../../../../../components/Spinner/Spinner';

const PRINTING_ORDERS = graphql(`
  query EventPrintingOrdersQuery($id: ID!, $cursor: String, $first: Int, $terms: [String!]) {
    event(id: $id) {
      # It is important to return the event id for the pagination cache to work properly
      id
      salesStats {
        local
      }
      printingOrders(after: $cursor, first: $first, terms: $terms) {
        edges {
          cursor
          node {
            id
            everyItemInvalidated
            orderedAt
            issuedBy {
              email
            }
            itemCount
            preferredBillingAddress {
              name
            }
            user {
              email
              name
            }
            tags
            comment
            emailDeliveries
            productVariant {
              description
              physical
              pricingCategory {
                name
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`);

interface Props {
  event: { id: string; venuePlan?: VenuePlan };
  onTicketUpdate: () => void;
  onTicketsRefund: (info: { generalSectionIds: Array<string>; seated: boolean }) => void;
  printingOrdersKey: number;
}

async function shareTickets(urls: string[]): Promise<void> {
  window.location.href = generateMailToLink({ email: [], subject: '', body: urls.join('\n') });
}

type PrintingOrder = Omit<GeneratedPrintingOrder, 'items'> & { items: PrintedTicket[] };

export function PrintHistory({ event, onTicketUpdate, onTicketsRefund, printingOrdersKey }: Props): JSX.Element {
  const [, setParams] = useQueryAsState();

  const { model, fields } = useForm<{
    terms?: string[] | null;
  }>({
    model: {},
  });

  useEffect(() => {
    setParams({ query: model.terms?.map((item) => ({ label: item, exclude: false })) });
  }, [model.terms]);

  // Printing Orders array
  const { data, loading, refetch, fetchMore } = useQueryHandler(PRINTING_ORDERS, {
    variables: { id: event.id, first: 10, terms: model.terms ?? [], cursor: null },
    preserveDataWhileLoading: true,
    reloadKey: printingOrdersKey,
  });

  const { pageInfo } = data?.event?.printingOrders || {};

  const [printingOrders, setPrintingOrders] = useMap<PrintingOrder>([]);

  useEffect(() => {
    const orders = (data?.event?.printingOrders?.edges.map((item) => item.node) as PrintingOrder[]) || [];
    setPrintingOrders(orders, true);
  }, [data]);

  // Ticket Selection
  const selectedTicketIds = useSelection<string>();
  const selectedOrderIds = useSelection<string>();

  const [selectedTickets, setSelectedTickets] = useState<PrintedTicket[]>([]);

  useEffect(() => {
    const tickets: PrintedTicket[] = [];

    printingOrders.forEach((order) => {
      order.items?.forEach((ticket) => {
        if (selectedTicketIds.has(ticket.id)) {
          tickets.push(ticket);
        }
      });
    });

    setSelectedTickets(tickets);
  }, [selectedTicketIds.size]);

  const [temporaryCheck, setTemporaryCheck] = useState<string[]>([]);

  async function toggleRowSelect(row: PrintingOrder): Promise<void> {
    const printingOrder = printingOrders.get(row.id);
    let currentRowTickets = printingOrder?.items || [];

    if (printingOrder?.items === undefined) {
      setTemporaryCheck([...temporaryCheck, row.id]);

      try {
        const result = await apollo.query({
          query: PRINT_ORDER,
          variables: {
            eventId: event.id,
            printingOrderId: row.id,
          },
        });

        const items = result.data?.event?.printingOrder?.items.nodes.filter(notNil) || [];
        currentRowTickets = items;

        if (printingOrder) {
          setPrintingOrders({
            ...printingOrder,
            items,
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        setTemporaryCheck(temporaryCheck.filter((e) => e !== row.id));
      }
    }

    selectedOrderIds.toggle(row.id);

    const validTickets = currentRowTickets.filter((item) => !item.invalidated).map((e) => e.id);

    if (selectedTicketIds.hasEvery(validTickets)) {
      selectedTicketIds.remove(validTickets);
    } else {
      selectedTicketIds.add(validTickets);
    }
  }

  function addTicketsToOrder(id: string, items: PrintedTicket[]): void {
    const printingOrder = printingOrders.get(id);

    if (printingOrder) {
      setPrintingOrders({
        ...printingOrder,
        items,
      });
    }
  }

  // Dialog Handling
  const [activeModalName, setActiveModalName] = useState<'return-tickets' | 'resend-email' | null>(null);

  const [resendEmailDetails, setResendEmailDetails] = useState<{
    orderId: string;
    email: string;
    noOfTickets: number;
    name?: string;
  }>();

  function triggerTicketsRefund(ids: string[]): void {
    selectedTicketIds.clear();
    selectedTicketIds.add(ids);
    setActiveModalName('return-tickets');
  }

  function onDialogSucess(): void {
    setActiveModalName(null);
    setResendEmailDetails(undefined);
    selectedTicketIds.clear();
    selectedOrderIds.clear();
  }

  function handleOnResendEmailSuccess(): void {
    const order = printingOrders.get(resendEmailDetails?.orderId || '');
    if (order) {
      setPrintingOrders({
        ...order,
        emailDeliveries: [...order.emailDeliveries, new Date().toISOString()],
      });
    }
    onDialogSucess();
  }

  const printingOrdersArray = Array.from(printingOrders.values());

  return (
    <Card
      id="print-history"
      title={
        selectedTicketIds.size ? (
          t.actions.selected(t.model.ticket.amount(selectedTicketIds.size))
        ) : (
          <span>
            {t.model.ticket.orderHistory} {!!data?.event?.salesStats?.local && <>({data?.event?.salesStats?.local})</>}
          </span>
        )
      }
      className={classnames({
        'print-history--with-selected-items': temporaryCheck.length || !!selectedTicketIds.size,
      })}
      actions={
        temporaryCheck.length ? (
          <Spinner color="white" />
        ) : (
          !!selectedTicketIds.size && (
            <div className="print-history--header-icon-wrapper">
              <Icon name="trash" tooltip={t.general.refund} onClick={() => setActiveModalName('return-tickets')} />
              {selectedTickets.every((e) => e?.url) && (
                <Icon
                  name="share"
                  tooltip={t.general.sendViaEmail}
                  onClick={() => shareTickets(compact(selectedTickets.map((e) => e.url)))}
                />
              )}
            </div>
          )
        )
      }
      footer={
        pageInfo?.hasNextPage && (
          <Button
            loading={loading}
            title={t.actions.loadMore}
            onClick={() =>
              fetchMore({
                variables: {
                  id: event.id,
                  cursor: pageInfo?.endCursor,
                },
              })
            }
          />
        )
      }
    >
      <Stack className="print-history__table">
        <Stack line>
          <FormTagsInput
            {...fields.terms}
            icon="search--sm"
            allowEmpty
            label={t.model.ticket.search}
            required
            maxWidth={325}
          />
          {data && loading && <Icon name="search" loading style={{ paddingTop: 22 }} />}
        </Stack>
        <SortableList<PrintingOrder & { actions?: unknown }>
          loading={loading && !data}
          emptyMessage={t.model.ticket.noTicketsIssued}
          columns={[
            {
              dataKey: 'orderedAt',
              label: t.model.datetime._name,
              cellRenderer: (row) => {
                return (
                  <div>
                    {formatDateTime(row.orderedAt)}
                    <div className="print-history__weak">{row.issuedBy?.email}</div>
                  </div>
                );
              },
            },
            {
              dataKey: 'itemCount',
              label: t.model.ticket.q,
              cellRenderer: (row) => row.itemCount,
            },
            {
              dataKey: 'user',
              label: t.model.ticket.recipient,
              cellRenderer: (row) => {
                if (!row.user) return 'N/A';

                return (
                  <div>
                    {row.preferredBillingAddress?.name}
                    {row.user?.email && <div className="print-history__weak">{row.user.email}</div>}
                  </div>
                );
              },
            },
            {
              dataKey: 'productVariant',
              label: t.model.ticket.pricingCategory,
              cellRenderer: (row) => {
                return (
                  <div>
                    {row.productVariant?.pricingCategory?.name}
                    <div className="print-history__weak">
                      {[row.productVariant?.description, t.model.ticket.onlineticket].join(' / ')}
                    </div>
                  </div>
                );
              },
            },
            {
              dataKey: 'tags',
              label: t.model.order.tags,
              cellRenderer: (row) => {
                return (
                  <Stack size="xxs" line wrap>
                    {row.tags?.map((tag) => (
                      <Tag key={tag} text={tag} />
                    ))}
                  </Stack>
                );
              },
            },
            {
              dataKey: 'comment',
              label: t.model.ticket.commentEmailText,
              cellRenderer: (row) => <TruncatedText text={row.comment} />,
            },
            {
              dataKey: 'emailDeliveries',
              label: t.model.ticket.emailDeliveryDates,
              cellRenderer: (row) => {
                if (!row.emailDeliveries?.length) return t.model.ticket.notAvailable;

                return (
                  <>
                    {row.emailDeliveries.map((emailDeliveryDate) => {
                      return (
                        <div key={emailDeliveryDate} className="print-history__date">
                          {formatDateTime(emailDeliveryDate)}
                        </div>
                      );
                    })}
                  </>
                );
              },
            },
            {
              dataKey: 'actions',
              cellRenderer: (row) => {
                if (row.everyItemInvalidated || !row.user?.email) return null;

                return (
                  <Icon
                    name="speedemail"
                    onClick={() => {
                      if (row.user?.email) {
                        setResendEmailDetails({
                          orderId: row.id,
                          email: row.user.email,
                          noOfTickets: row.itemCount,
                          name: row.preferredBillingAddress?.name || '',
                        });
                        setActiveModalName('resend-email');
                      }
                    }}
                  />
                );
              },
              align: 'right',
            },
          ]}
          items={printingOrdersArray}
          fullBleed
          multiSelect
          selectedItems={printingOrdersArray.filter((e) => selectedOrderIds.has(e.id))}
          onRowSelect={(row) => {
            toggleRowSelect(row);
          }}
          rowClass={(row) => row.everyItemInvalidated && 'strikethrough'}
          expandable={() => true}
          expandedContent={(row) => {
            return (
              <OrderTickets
                eventId={event.id}
                printingOrderId={row.id}
                selectedTicketIds={selectedTicketIds}
                selectedOrderIds={selectedOrderIds}
                onTicketUpdate={onTicketUpdate}
                onDeleteTicket={(ticketId, tickets) => {
                  triggerTicketsRefund([ticketId]);
                  addTicketsToOrder(row.id, tickets);
                }}
                onTicketsLoaded={(tickets) => addTicketsToOrder(row.id, tickets)}
              />
            );
          }}
          compactExpandableContent
          footerLabel={t.general.total}
          disableSelectForRow={(row) => !!row.everyItemInvalidated}
          forceCheckForRow={(row) => temporaryCheck.includes(row.id)}
        />
      </Stack>
      <Modal open={activeModalName === 'return-tickets'} size="small" title={t.model.ticket.cancelTickets}>
        <TicketReturn
          event={event}
          tickets={selectedTickets}
          onClose={() => {
            setActiveModalName(null);
          }}
          onSuccess={({ seated, generalSectionIds }) => {
            refetch();
            onTicketUpdate();
            onTicketsRefund({ seated, generalSectionIds });
            onDialogSucess();
          }}
        />
      </Modal>
      <Modal title={t.model.resendTickets.confirmResendingEmail} open={activeModalName === 'resend-email'} size="xs">
        {resendEmailDetails && (
          <ResendEmailTickets
            details={resendEmailDetails}
            onClose={() => {
              setActiveModalName(null);
            }}
            onSuccess={handleOnResendEmailSuccess}
          />
        )}
      </Modal>
    </Card>
  );
}
